import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLang, t } from '../context/LanguageContext';
import translations from '../i18n/translations';
import { Send, CheckCircle, Loader2, UserCheck, Building2 } from 'lucide-react';


const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const PREFIXES = ['M.', 'Mme', 'Mlle', 'Dr', 'Prof.', 'Rev.'];
const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
const MONTHS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
const MONTH_LABELS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const MONTH_LABELS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 3 }, (_, i) => String(currentYear + i));

export default function Leads() {
  const { lang } = useLang();
  const l = translations.leads;
  const [form, setForm] = useState({
    // Partenaire
    partenaire_prenom: '',
    partenaire_nom: '',
    partenaire_email: '',
    // Destinataire
    entreprise: '',
    contact_prefix: 'M.',
    contact_prenom: '',
    contact_nom: '',
    contact_email: '',
    contact_email_confirm: '',
    contact_poste: '',
    contact_telephone: '',
    description: '',
    rappel_jour: '',
    rappel_mois: '',
    rappel_annee: '',
  });
  const [status, setStatus] = useState('idle');
  const [emailMismatch, setEmailMismatch] = useState(false);

  const handleChange = (e) => {
    const updated = { ...form, [e.target.name]: e.target.value };
    setForm(updated);
    if (e.target.name === 'contact_email_confirm' || e.target.name === 'contact_email') {
      setEmailMismatch(
        updated.contact_email !== '' &&
        updated.contact_email_confirm !== '' &&
        updated.contact_email !== updated.contact_email_confirm
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.contact_email !== form.contact_email_confirm) {
      setEmailMismatch(true);
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch(`/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, lang }),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputClass = "w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-lg p-4 text-white placeholder-slate-500 focus:ring-1 focus:ring-[#0099D6] transition-all outline-none";
  const labelClass = "block text-sm font-medium text-slate-300 mb-2";
  const monthLabels = lang === 'fr' ? MONTH_LABELS_FR : MONTH_LABELS_EN;

  return (
    <main data-testid="leads-page">
      {/* Hero */}
      <section className="pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-4">MÉTÉRIS</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-6">{t(l.title, lang)}</h1>
            <p className="text-xl text-slate-300 max-w-2xl">{t(l.subtitle, lang)}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {status === 'success' ? (
            <motion.div {...fadeUp} className="bg-[#15171E]/80 backdrop-blur-xl border border-emerald-500/20 rounded-2xl p-12 text-center">
              <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-white mb-3">{t(l.success_title, lang)}</h3>
              <p className="text-slate-300">{t(l.success_text, lang)}</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-10" data-testid="leads-form">

              {/* Section Partenaire */}
              <motion.div {...fadeUp} className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <UserCheck className="w-6 h-6 text-[#0099D6]" />
                  <h2 className="text-xl font-semibold text-white">{t(l.section_partner, lang)}</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className={labelClass}>{t(l.prenom, lang)} *</label>
                    <input type="text" name="partenaire_prenom" value={form.partenaire_prenom} onChange={handleChange} required className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t(l.nom, lang)} *</label>
                    <input type="text" name="partenaire_nom" value={form.partenaire_nom} onChange={handleChange} required className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t(l.email, lang)}</label>
                    <input type="email" name="partenaire_email" value={form.partenaire_email} onChange={handleChange} className={inputClass} />
                  </div>
                </div>
              </motion.div>

              {/* Section Destinataire */}
              <motion.div {...fadeUp} className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Building2 className="w-6 h-6 text-[#0099D6]" />
                  <h2 className="text-xl font-semibold text-white">{t(l.section_recipient, lang)}</h2>
                </div>
                <div className="space-y-5">
                  {/* Entreprise */}
                  <div>
                    <label className={labelClass}>{t(l.entreprise, lang)}</label>
                    <input type="text" name="entreprise" value={form.entreprise} onChange={handleChange} className={inputClass} />
                  </div>

                  {/* Contact : préfixe + prénom + nom */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>{t(l.prefix, lang)} *</label>
                      <select name="contact_prefix" value={form.contact_prefix} onChange={handleChange} className={inputClass}>
                        {PREFIXES.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>{t(l.prenom, lang)} *</label>
                      <input type="text" name="contact_prenom" value={form.contact_prenom} onChange={handleChange} required className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>{t(l.nom, lang)} *</label>
                      <input type="text" name="contact_nom" value={form.contact_nom} onChange={handleChange} required className={inputClass} />
                    </div>
                  </div>

                  {/* Email + confirmation */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>{t(l.email, lang)} *</label>
                      <input type="email" name="contact_email" value={form.contact_email} onChange={handleChange} required className={`${inputClass} ${emailMismatch ? 'border-red-500' : ''}`} />
                    </div>
                    <div>
                      <label className={labelClass}>{t(l.email_confirm, lang)} *</label>
                      <input type="email" name="contact_email_confirm" value={form.contact_email_confirm} onChange={handleChange} required className={`${inputClass} ${emailMismatch ? 'border-red-500' : ''}`} />
                      {emailMismatch && <p className="text-red-400 text-xs mt-1">{lang === 'fr' ? 'Les emails ne correspondent pas.' : 'Emails do not match.'}</p>}
                    </div>
                  </div>

                  {/* Poste + Téléphone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>{t(l.poste, lang)}</label>
                      <input type="text" name="contact_poste" value={form.contact_poste} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>{t(l.telephone, lang)} *</label>
                      <input type="tel" name="contact_telephone" value={form.contact_telephone} onChange={handleChange} required className={inputClass} />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className={labelClass}>{t(l.description, lang)}</label>
                    <textarea name="description" value={form.description} onChange={handleChange} rows={6} className={`${inputClass} resize-none`} />
                  </div>

                  {/* Date de rappel */}
                  <div>
                    <label className={labelClass}>{t(l.rappel_date, lang)} *</label>
                    <div className="grid grid-cols-3 gap-3">
                      <select name="rappel_jour" value={form.rappel_jour} onChange={handleChange} required className={inputClass}>
                        <option value="">{lang === 'fr' ? 'Jour' : 'Day'}</option>
                        {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <select name="rappel_mois" value={form.rappel_mois} onChange={handleChange} required className={inputClass}>
                        <option value="">{lang === 'fr' ? 'Mois' : 'Month'}</option>
                        {MONTHS.map((m, i) => <option key={m} value={m}>{monthLabels[i]}</option>)}
                      </select>
                      <select name="rappel_annee" value={form.rappel_annee} onChange={handleChange} required className={inputClass}>
                        <option value="">{lang === 'fr' ? 'Année' : 'Year'}</option>
                        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div {...fadeUp}>
                <button
                  type="submit"
                  disabled={status === 'loading' || emailMismatch}
                  className="inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-4 px-8 rounded-lg shadow-[0_0_20px_rgba(0,153,214,0.3)] transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  {t(l.submit, lang)}
                </button>
                {status === 'error' && (
                  <p className="text-red-400 text-sm mt-3">{lang === 'fr' ? 'Erreur. Veuillez réessayer.' : 'Error. Please try again.'}</p>
                )}
              </motion.div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
